import {
  Component, EventEmitter, Input, OnInit, Output, SimpleChanges, OnChanges
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { take, takeUntil, filter } from 'rxjs/operators';

import { SortOrderValueKey } from 'graphql/models/extended';
import { AccountType, SortOrder } from 'graphql/models/generated';
import { AccountTypeENUM } from 'models/account.model';
import {
  DataTableResourceType, DataItem, DataTableConfig,
  DataTableAction, DataTableActionType
} from 'models/data.model';
import { DropdownConfig } from 'models/dropdown.model';
import { ImportJobs } from 'models/import-job.model';
import { TableLayoutMod } from 'models/layout-mod.model';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { PermissionsService } from 'shared/services/permissions.service';
import { StoreService } from 'shared/services/store.service';
import { StylingService } from 'shared/services/styling.service';
import { DATA_TABLE_CONFIG } from 'shared/table/table-form/table-form-config';

@Component({
  selector: 'app-table-form',
  templateUrl: './table-form.component.html',
  styleUrls: ['./table-form.component.scss']
})
export class TableFormComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() resourceType!: DataTableResourceType;

  @Input() dropdownFilters: DropdownConfig[] = [];

  @Input() additionalFilters: Record<string, any> = {};

  @Input() resourceId = '';

  @Input() preselectedId = '';

  @Input() preselectFirst = false;

  @Input() layoutMod: TableLayoutMod = 'wide';

  @Input() showHeaderActions = true;

  @Output() items = new EventEmitter<DataItem[]>();

  @Output() rowSelected = new EventEmitter<DataItem>();

  @Output() actionSelected = new EventEmitter<{ actionType: DataTableActionType; selectedRow: DataItem }>();

  data!: DataItem[];

  displayedColumns!: string[];

  selectedRow!: DataItem;

  layoutModifier = '';

  inputSearchLayoutMod = '';

  searchTerm = '';

  filters: Record<string, any> = {};

  config!: DataTableConfig;

  perPage = 10;

  currentPage = 1;

  sortKey!: string[];

  sortDirection!: SortOrder;

  showPagination = false;

  isNextPageDisabled = true;

  isPrevPageDisabled = true;

  form!: UntypedFormGroup;

  isDealer!: boolean;

  accountType!: AccountType;

  get filtersForm(): UntypedFormGroup {
    return this.form.controls.filters as UntypedFormGroup;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private stylingService: StylingService,
    private coreDataService: CoreDataService,
    private storeService: StoreService,
    private permissions: PermissionsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.config = DATA_TABLE_CONFIG[this.resourceType];

    combineLatest([
      this.storeService.isCurrentAccountDealer(),
      this.storeService.getImportJob()
    ])
      .pipe(
        filter(([importJob]:
          [boolean, ImportJobs]) => Object.keys(importJob).length === 0),
        takeUntil(this.destroyed$)
      )
      .subscribe(([isDealer]: [boolean, ImportJobs]) => {
        this.isDealer = isDealer;
        this.accountType = this.isDealer ? AccountTypeENUM.DEALER : AccountTypeENUM.MANUFACTURER;

        switch (this.resourceType) {
          case 'Account':
            if (!this.isDealer) {
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'branches');
            }
            break;
          case 'Article':
            if (!this.isDealer) {
              // purchasePrice is not usable on manufacture side
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'purchasePrice');
            }
            this.filterActions(this.permissions.cannotUpdateArticles(), this.permissions.cannotDeleteArticles());
            break;
          case 'Condition':
            if (!this.isDealer) {
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'customerNumber');
            } else {
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'id');
            }
            this.filterActions(this.permissions.cannotUpdateConditions(), this.permissions.cannotDeleteConditions());
            break;
          case 'ContactPerson':
            this.filterActions(
              this.permissions.cannotUpdateContactPerson(),
              this.permissions.cannotDeleteContactPerson()
            );
            break;
          case 'Customer':
            if (!this.isDealer) {
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'customerNumber');
            } else {
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'id');
            }
            this.filterActions(this.permissions.cannotUpdateCustomer(), this.permissions.cannotDeleteCustomer());
            break;
          case 'Object':
            if (!this.isDealer) {
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'nameForDealer');
              if (this.config.actions) {
                this.config.actions = this.config.actions.filter((action) => action.value !== 'acceptObject');
              }
            } else {
              this.config.displayedColumns = this.config.displayedColumns.filter((col) => col !== 'name');
            }
            this.filterActions(this.permissions.cannotUpdateObjekt(), this.permissions.cannotDeleteObjekt());
            break;
          default:
            break;
        }

        this.displayedColumns = this.layoutMod === 'wide'
          ? ['checkmark', ...this.config.displayedColumns]
          : [
            ...this.config.displayedColumns,
            ...(this.config.actions ? ['actions'] : [])
          ];

        this.layoutModifier = this.stylingService.concatLayoutModifiers('tableForm', [this.layoutMod]);
        this.inputSearchLayoutMod = this.layoutMod;

        this.setupForm();
        this.getData();
      });

    this.storeService.triggerTableUpdate()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.currentPage = 1;
        this.getData();
        this.storeService.setTableUpdate(false);
      });
  }

  private filterActions(cannotUpdate: boolean, cannotDelete: boolean): void {
    this.config.actions = (this.config.actions || [])
      .filter((a) => {
        if ((a.value === 'edit' && cannotUpdate) || (a.value === 'delete' && cannotDelete)) {
          return false;
        }

        return true;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.additionalFilters && Object.keys(changes.additionalFilters.currentValue).length) {
      this.getData();
    }
  }

  setupForm(): void {
    const filtersForm = this.fb.group({});

    // eslint-disable-next-line @typescript-eslint/no-shadow
    this.dropdownFilters.forEach((filter: DropdownConfig) => {
      filtersForm.addControl(filter.controlName, this.fb.control(filter.defaultValue || null));
    });

    this.form = this.fb.group({
      itemsPerPage: [this.perPage, Validators.required],
      filters: filtersForm
    });

    this.form.controls.itemsPerPage.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.perPage = value;
        this.currentPage = 1;

        this.getData();
      });

    this.filtersForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((values) => {
        this.filters = values;
        this.currentPage = 1;

        this.getData();
      });
  }

  private setSelectedRow(selectedRow?: DataItem): void {
    // the row that was previously selected needs to be remembered,
    // and set again as "row"
    let row = selectedRow;

    this.preselectedId = (row && row.id) ? row.id : this.preselectedId;

    if (this.layoutMod === 'wide' && row === undefined) {
      if (this.preselectFirst && !this.preselectedId) {
        row = this.data[0];
      } else {
        row = this.data.find((item) => item.id === this.preselectedId);
      }
    }

    this.selectedRow = row as DataItem;
    this.rowSelected.emit(this.selectedRow);
  }

  searchData(searchTerm: string): void {
    this.searchTerm = searchTerm.trim();
    this.currentPage = 1;
    this.getData();
  }

  getData(): void {
    let filters = {};
    const pageParam = this.resourceType === 'LooseArticle'
      ? this.currentPage - 1 : this.perPage * (this.currentPage - 1);
    if (Object.keys(this.additionalFilters).length) {
      filters = this.additionalFilters;
    } else if (Object.keys(this.filters).length) {
      filters = this.filters;
    }

    const orderBy = (this.sortKey !== undefined
      ? this.sortKey.map((key: string) => ({ [key]: this.sortDirection }))
      : undefined);

    this.coreDataService.getItemsByResourceType({
      type: this.resourceType,
      id: this.resourceId,
      take: this.perPage,
      skip: pageParam,
      order: orderBy,
      searchTerm: this.searchTerm,
      filters,
      accountType: this.accountType
    })
      .pipe(take(1))
      .subscribe((data: DataItem[]) => {
        this.data = data;

        if (this.resourceType === 'Account') {
          this.data = this.data.map(user => {
            const canEdit = this.permissions.canEditUser(user);
            return ({
              ...user,
              canEdit: canEdit,
            });
          });
        }

        if (this.layoutMod === 'wide') {
          this.setSelectedRow();
        }

        this.isPrevPageDisabled = this.currentPage === 1;
        this.isNextPageDisabled = data.length < this.perPage;

        if (this.currentPage === 1 && data.length < 10) {
          this.showPagination = false;
        } else {
          this.checkPrevNextButtons();
        }

        this.items.emit(this.data);
      });
  }

  onRowSelect(row: DataItem): void {
    // when a user goes through this flow:
    // 1) search something
    // 2) do not select a row
    // 3) clear search query
    this.setSelectedRow(row);
  }

  sortBy(sortKey: string[]): void {
    this.currentPage = 1;
    if (this.sortKey !== sortKey) {
      this.sortKey = sortKey;
      this.sortDirection = SortOrderValueKey.ASC;
    } else {
      this.sortDirection = (this.sortDirection === SortOrderValueKey.ASC
        ? SortOrderValueKey.DESC
        : SortOrderValueKey.ASC
      );
    }
    this.getData();
  }

  onActionSelect(action: DataTableAction, row: DataItem): void {
    this.actionSelected.emit({ actionType: action.value, selectedRow: row });
  }

  inviteAgain(row: DataItem) {
    this.actionSelected.emit({ actionType: 'reinvite', selectedRow: row });
  }

  previousPage(): void {
    this.currentPage -= 1;
    this.getData();
  }

  nextPage(): void {
    this.currentPage += 1;
    this.getData();
  }

  checkPrevNextButtons(): void {
    this.isPrevPageDisabled = this.currentPage === 1;
    const skipCount = this.resourceType === 'LooseArticle'
      ? this.currentPage
      : this.perPage * (this.currentPage);

    this.coreDataService
      .getItemsByResourceType({
        type: this.resourceType,
        id: this.resourceId,
        take: this.perPage,
        skip: skipCount,
        searchTerm: this.searchTerm,
        accountType: this.accountType
      })
      .pipe(take(1))
      .subscribe((data: DataItem[]) => {
        if (this.currentPage === 1 && data.length) {
          this.showPagination = true;
        }
        this.isNextPageDisabled = !data.length;
      });
  }
}
